import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../../components/button/Button";
import AuthTextInput from "../../../../components/inputs/authTextInput/AuthTextInput";
import DateInput from "../../../../components/inputs/dateInput/DateInput";
import ImagesDropdown from "../../../../components/inputs/imagesDropdown/ImagesDropdown";
import NumberInput from "../../../../components/inputs/numberInput/NumberInput";
import SelectInput from "../../../../components/inputs/selectInput/SelectInput";
import Loader from "../../../../components/loader/Loader";
import SettingsPagesContainer from "../../../../components/settingsPagesContainer/SettingsPagesContainer";
import TextWrapper from "../../../../components/textWrapper/TextWrapper";
import { fetchTournamentImages } from "../../../../features/images/tournamentImages/tournamentImagesSlice";
import { fetchRoomSets } from "../../../../features/roomSets/roomSetSlice";
import {
  createTournament,
  fetchTournamentsStatuses,
} from "../../../../features/tournaments/tournamentsSlice";
import { fetchTournamentDetailsTemplateOptions } from "../../../../features/tournamentDetailsTemplates/tournamentDetailsTemplatesSlice";
import { getUTCDateWithPeakTimeFromDate } from "../../../../utils/timeZone";
import styles from "./addNewTournament.module.css";
import { fetchRegions } from "../../../../features/regions/regionsSlice";

// The room times each game plays: Free Fire is always 17 minutes; a PUBG
// tournament has its own chosen (25 or 40), never one left from another game.
// It picks the tournament's timeline (constants/timelines*.js on the backend).
const ROOM_TIMES = { freefire: [17], pubg: [25, 40] };

const AddNewTournament = () => {
  const [tournamentStatus, setTournamentStatus] = useState("");
  const [tournamentName, setTournamentName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [prize, setPrize] = useState(0);
  const [image, setImage] = useState("");
  const [roomSetsDistributions, setRoomSetsDistributions] = useState([]);
  const [detailsTemplate, setDetailsTemplate] = useState("");
  const adsCount = 5;
  const [gameName, setGameName] = useState("");
  const [roomTime, setRoomTime] = useState(null);
  // After a save attempt, a missing room time is pointed out.
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [tournamentType, setTournamentType] = useState("solo");
  const [regionRequiredAds, setRegionRequiredAds] = useState({});

  const dispatch = useDispatch();

  const { fetchStatusesStatus, statusesList, addStatus } = useSelector(
    (state) => state.tournaments,
  );

  const { fetchStatus: fetchDistributionsStatus, list: roomSets } = useSelector(
    (state) => state.roomSets,
  );

  const { list: regionsList, fetchStatus: regionFetchStatus } = useSelector(
    (state) => state.regions,
  );

  const { fetchStatus: imagesFetchStatus, list: tournamentImages } =
    useSelector((state) => state.tournamentImages);

  const { options: templateOptions, optionsFetchStatus: templatesFetchStatus } =
    useSelector((state) => state.tournamentDetailsTemplates);

  const roomTimeValid = (ROOM_TIMES[gameName] || []).includes(Number(roomTime));

  // A new game resets the room time: Free Fire's own, PUBG to be chosen.
  const handleGameChange = (nextGame) => {
    if (nextGame === gameName) return;
    setGameName(nextGame);
    setRoomTime(nextGame === "freefire" ? 17 : null);
  };

  const handleCreateNewTournamentSubmit = () => {
    setTriedSubmit(true);
    if (
      tournamentStatus !== "" &&
      tournamentName !== "" &&
      startDate !== "" &&
      prize !== "" &&
      image !== "" &&
      gameName !== "" &&
      roomTimeValid
    ) {
      const adminId = localStorage.getItem("adminId");
      const selectedRoomSetsDistributions = roomSetsDistributions.filter(
        (roomSetId) => String(roomSetId || "").trim() !== "",
      );

      const requiredAdsByRegionPayload = Object.entries(regionRequiredAds)
        .filter(([, v]) => Number(v) > 0)
        .map(([regionId, requiredAds]) => ({
          region: regionId,
          requiredAds: Number(requiredAds),
        }));

      dispatch(
        createTournament({
          status: tournamentStatus,
          name: tournamentName,
          createdBy: adminId,
          startDate: getUTCDateWithPeakTimeFromDate(startDate),
          prize,
          tournamentImage: image._id,
          roomSetsDistributions: selectedRoomSetsDistributions,
          detailsTemplate: detailsTemplate || null,
          adsCount,
          gameName,
          roomTime: Number(roomTime),
          requiredAdsByRegion: requiredAdsByRegionPayload,
          tournamentType,
          maxUsersByTeam: tournamentType === "squad" ? 4 : 1,
        }),
      );
    }
  };

  const handleAddRoomSetDistribution = () => {
    setRoomSetsDistributions((prev) => [...prev, ""]);
  };

  const handleUpdateRoomSetDistribution = (index) => (nextValue) => {
    setRoomSetsDistributions((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? nextValue : item)),
    );
  };

  const handleUpdateRegionRequiredAds = (regionId) => (value) => {
    const nextValue = Math.max(0, Number(value || 0));

    setRegionRequiredAds((prev) => ({
      ...prev,
      [String(regionId)]: nextValue,
    }));
  };

  useEffect(() => {
    if (fetchStatusesStatus === "idle") {
      dispatch(fetchTournamentsStatuses());
    }

    if (imagesFetchStatus === "idle") {
      dispatch(fetchTournamentImages());
    }

    if (fetchDistributionsStatus === "idle") {
      dispatch(fetchRoomSets());
    }

    if (regionFetchStatus === "idle") {
      dispatch(fetchRegions());
    }

    if (templatesFetchStatus === "idle") {
      dispatch(fetchTournamentDetailsTemplateOptions());
    }
  }, [
    dispatch,
    fetchDistributionsStatus,
    fetchStatusesStatus,
    imagesFetchStatus,
    regionFetchStatus,
    templatesFetchStatus,
  ]);

  useEffect(() => {
    if (!Array.isArray(regionsList) || regionsList.length === 0) return;

    setRegionRequiredAds((prev) => {
      const next = { ...prev };

      regionsList.forEach((r) => {
        const id = String(r._id);
        if (next[id] === undefined) next[id] = 0;
      });

      return next;
    });
  }, [regionsList]);

  if (
    imagesFetchStatus === "loading" ||
    fetchStatusesStatus === "loading" ||
    addStatus === "loading" ||
    fetchDistributionsStatus === "loading" ||
    regionFetchStatus === "loading" ||
    templatesFetchStatus === "loading"
  ) {
    return (
      <div className={styles.loaderContainer}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <SettingsPagesContainer
        title="اضافة بطولة جديدة"
        fullHeight
        displayContainerClassName={styles.displayContainer}
      >
        <div className={styles.formLayout}>
          <div className={styles.fieldsWrapper}>
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <TextWrapper textStyle="textMid">المعلومات الأساسية</TextWrapper>
              </div>
              <div className={styles.sectionGrid}>
                <div className={`${styles.inputWrapper} ${styles.fullSpan}`}>
                  <TextWrapper textStyle="textMid">اسم البطولة</TextWrapper>
                  <AuthTextInput
                    height="52px"
                    width="100%"
                    borderRadius="12px"
                    placeholder="اسم البطولة"
                    bgColor={"grey"}
                    value={tournamentName}
                    setValue={setTournamentName}
                  />
                </div>
                <div className={styles.inputWrapper}>
                  <TextWrapper textStyle="textMid">اسم اللعبة</TextWrapper>
                  <SelectInput
                    placeHolder={"اسم اللعبة"}
                    value={gameName}
                    setValue={handleGameChange}
                    wrapperClassName={styles.fullWidthField}
                    className={styles.fullWidthSelectButton}
                  >
                    <option value={"pubg"} key={"pubg"}>
                      PUBG
                    </option>
                    <option value={"freefire"} key={"freefire"}>
                      Free Fire
                    </option>
                  </SelectInput>
                </div>
                {gameName === "pubg" && (
                  <div className={styles.inputWrapper}>
                    <TextWrapper textStyle="textMid">وقت الروم</TextWrapper>
                    <SelectInput
                      placeHolder={"وقت الروم"}
                      value={roomTime}
                      setValue={setRoomTime}
                      noPlaceHolder
                      wrapperClassName={styles.fullWidthField}
                      className={styles.fullWidthSelectButton}
                    >
                      <option value={25} key={25}>
                        25
                      </option>
                      <option value={40} key={40}>
                        40
                      </option>
                    </SelectInput>
                    {triedSubmit && !roomTimeValid && (
                      <span className={styles.fieldError} role="alert">
                        اختر وقت الروم: 25 أو 40 دقيقة
                      </span>
                    )}
                  </div>
                )}
                <div className={styles.inputWrapper}>
                  <TextWrapper textStyle="textMid">حالة البطولة</TextWrapper>
                  <SelectInput
                    placeHolder={"حالة البطولة"}
                    value={tournamentStatus}
                    setValue={setTournamentStatus}
                    wrapperClassName={styles.fullWidthField}
                    className={styles.fullWidthSelectButton}
                  >
                    {Array.isArray(statusesList) &&
                      statusesList.map((item) => {
                        return (
                          <option value={item} key={item}>
                            {item === "upcoming"
                              ? "قادمة"
                              : item === "live"
                                ? "مباشرة"
                                : "منتهية"}
                          </option>
                        );
                      })}
                  </SelectInput>
                </div>
                <div className={styles.inputWrapper}>
                  <TextWrapper textStyle="textMid">نوع البطولة</TextWrapper>
                  <SelectInput
                    placeHolder={"نوع البطولة"}
                    value={tournamentType}
                    setValue={setTournamentType}
                    noPlaceHolder
                    wrapperClassName={styles.fullWidthField}
                    className={styles.fullWidthSelectButton}
                  >
                    <option value={"solo"} key={"solo"}>
                      فردي
                    </option>
                    <option value={"squad"} key={"squad"}>
                      سكواد
                    </option>
                  </SelectInput>
                </div>
                <div className={styles.inputWrapper}>
                  <TextWrapper textStyle="textMid">تاريخ البداية</TextWrapper>
                  <DateInput
                    value={startDate}
                    setValue={setStartDate}
                    className={styles.fullWidthControl}
                  />
                </div>
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <TextWrapper textStyle="textMid">
                  الإعلانات المطلوبة حسب المنطقة
                </TextWrapper>
              </div>
              <div className={styles.adsGrid}>
                {regionsList.map((region) => (
                  <div
                    className={`${styles.inputWrapper} ${styles.adsField}`}
                    key={region._id}
                  >
                    <TextWrapper textStyle="textMid">
                      عدد اعلانات {region?.name}
                    </TextWrapper>
                    <NumberInput
                      placeHolder={"عدد اعلانات البطولة"}
                      value={regionRequiredAds[String(region._id)] ?? 0}
                      setValue={handleUpdateRegionRequiredAds(region._id)}
                      className={styles.fullWidthControl}
                    />
                  </div>
                ))}
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <TextWrapper textStyle="textMid">الجائزة والقالب</TextWrapper>
              </div>
              <div className={styles.sectionGrid}>
                <div className={styles.inputWrapper}>
                  <TextWrapper textStyle="textMid">جائزة البطولة</TextWrapper>
                  <NumberInput
                    placeHolder={"جائزة البطولة"}
                    value={prize}
                    setValue={setPrize}
                    className={styles.fullWidthControl}
                  />
                </div>
                <div className={styles.inputWrapper}>
                  <TextWrapper textStyle="textMid">
                    قالب تفاصيل البطولة
                  </TextWrapper>
                  <SelectInput
                    placeHolder={"اختر قالب التفاصيل (اختياري)"}
                    value={detailsTemplate}
                    setValue={setDetailsTemplate}
                    wrapperClassName={styles.fullWidthField}
                    className={styles.fullWidthSelectButton}
                  >
                    {Array.isArray(templateOptions) &&
                      templateOptions.map((template) => (
                        <option value={template._id} key={template._id}>
                          {template.name}
                        </option>
                      ))}
                  </SelectInput>
                </div>
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <TextWrapper textStyle="textMid">توزيعات الغرف</TextWrapper>
                <div className={styles.sectionActionRow}>
                  <Button
                    size="circle"
                    variant="surfaceVariant"
                    functionallity={handleAddRoomSetDistribution}
                  >
                    <TextWrapper textStyle="textTitle">+</TextWrapper>
                  </Button>
                </div>
              </div>
              <div className={styles.roomSetList}>
                {roomSetsDistributions.map((item, index) => {
                  return (
                    <SelectInput
                      key={index}
                      value={item}
                      setValue={handleUpdateRoomSetDistribution(index)}
                      placeHolder={"اختيار توزيع الغرف"}
                      wrapperClassName={styles.fullWidthField}
                      className={styles.fullWidthSelectButton}
                    >
                      {Array.isArray(roomSets) &&
                        roomSets.map((roomSet) => {
                          return (
                            <option value={roomSet._id} key={roomSet._id}>
                              {roomSet.roomSet}
                            </option>
                          );
                        })}
                    </SelectInput>
                  );
                })}
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <TextWrapper textStyle="textMid">صورة البطولة</TextWrapper>
              </div>
              <ImagesDropdown
                data={tournamentImages}
                value={image}
                setValue={setImage}
                className={styles.imagesGrid}
                itemClassName={styles.imageOption}
              />
            </section>
          </div>
          <div className={styles.createNewTournamentBtnWrapper}>
            <Button
              size="bigSize"
              variant="surfaceVariant"
              functionallity={handleCreateNewTournamentSubmit}
            >
              <TextWrapper textStyle="textMid">
                تسجيل البطولة الجديدة
              </TextWrapper>
            </Button>
          </div>
        </div>
      </SettingsPagesContainer>
    </div>
  );
};

export default AddNewTournament;
